package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.GivingStatementBatchSummary
import com.dotdrives.repository.DonationRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlin.time.Instant

data class YearEndReceiptsUiState(
    val loading: Boolean = false,
    val year: Int? = null,
    val summary: GivingStatementBatchSummary? = null,
    val sending: Boolean = false,
    val sentCount: Int? = null,
    val error: String? = null,
)

/** Saves immutable year-end snapshots and queues deduplicated email; provider acceptance is separate. */
class YearEndReceiptsController(
    private val repo: DonationRepository,
    scope: CoroutineScope,
    private val orgId: String,
    private val clock: () -> Instant = { Clock.System.now() },
) : DotController<YearEndReceiptsUiState>(YearEndReceiptsUiState(), scope) {
    private var generation = 0L

    fun load(year: Int) {
        val gen = nextGeneration()
        update { it.copy(loading = true, year = year, sentCount = null, error = null) }
        scope.launch {
            try {
                val result = repo.statementBatchSummary(orgId, year)
                if (!isCurrent(gen)) return@launch
                result
                    .onSuccess { summary ->
                        update { if (isCurrent(gen)) it.copy(loading = false, summary = summary, error = null) else it }
                    }
                    .onFailure { publishError(it, gen) }
            } catch (error: Exception) {
                publishError(error, gen)
            }
        }
    }

    fun retry() {
        state.value.year?.let(::load)
    }

    fun send() {
        val summary = state.value.summary ?: return
        if (state.value.sending) return
        // Own the "no existing statement yet" filter here, not in the client, so it is a
        // controller-level fact that can be asserted on with a call recorder rather than
        // buried inside a fake handler or an untested Supabase-calling method.
        val pending = summary.drafts.filterNot { it.giverId in summary.alreadyGeneratedGiverIds }
        if (pending.isEmpty()) {
            update { it.copy(sentCount = 0, error = null) }
            return
        }
        update { it.copy(sending = true, error = null) }
        scope.launch {
            repo.generateStatements(orgId, summary.year, pending)
                .onSuccess { generated ->
                    update {
                        it.copy(
                            sending = false,
                            sentCount = generated.size,
                            summary = it.summary?.copy(
                                alreadyGeneratedGiverIds = it.summary.alreadyGeneratedGiverIds + generated.map { g -> g.giverId },
                                statements = (it.summary.statements + generated).distinctBy { s -> s.id },
                            ),
                            error = null,
                        )
                    }
                }
                .onFailure { error ->
                    if (error is CancellationException) throw error
                    update { it.copy(sending = false, error = error.message ?: "Something went wrong") }
                }
        }
    }

    private fun publishError(error: Throwable, generation: Long) {
        if (error is CancellationException) throw error
        if (!isCurrent(generation)) return
        update { if (isCurrent(generation)) it.copy(loading = false, error = error.message ?: "Something went wrong") else it }
    }

    private fun nextGeneration(): Long {
        generation += 1
        return generation
    }

    private fun isCurrent(generation: Long): Boolean = generation == this.generation
}
